import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import { Stack } from "@mui/material";
import HeaderSection from "./HeaderSection/HeaderSection";
import NoticeBanner from "../shared/NoticeBanner";
import ActionButton from "../shared/ActionButton";
import SectionTitle from "../shared/SectionTitle";
import ProfileSection from "./ProfileSection/ProfileSection";
import ProfileTextSection from "./ProfileTextSection/ProfileTextSection";
import QualificationSection from "./QualificationSection/QualificationSection";
import HumanSkillSection from "./HumanSkillSection/HumanSkillSection";
import SkillSection from "./SkillSection/SkillSection";
import SkillSheetMainSection from "./SkillSheetMainSection/SkillSheetMainSection";
import getPageType from "../utils/pageType";
import useScrollToAnchor from "../shared/hooks/useScrollToAnchor";
import { useIsDesktop } from "../shared/hooks/useIsDesktop";
import {
  PROJECT_COLOR,
  PROJECT_COLOR_DARK,
  JOB_COLOR,
  JOB_COLOR_DARK,
} from "../../constants/colors";
import {
  targetFromFormData,
  editSkillSheetBasicsPathFor,
} from "../utils/skillSheetRouting";
import { toast } from "react-toastify";

// 画面種別とIDから取得するAPI URLを決定
const buildSkillSheetApiUrl = ({ pageType, targetId }) => {
  switch (pageType) {
    case "management_company":
      return `/company/members/${targetId}/skill_sheet.json`;
    case "recruit_company":
      return `/company/candidates/${targetId}/skill_sheet.json`;
    case "admin":
      return `/user_show/${targetId}.json`;
    default:
      return "/skill_sheets.json";
  }
};

// APIレスポンスをMyPage用の形に正規化
const normalizeSkillSheetJson = (json) => ({
  profile: json?.profile ?? {},
  skill: json?.skill ?? {},
  workHistories: json?.work_histories ?? [],
  pdfDownloadUrl: json?.pdf_download_url ?? null,
  showEditButton: json?.show_edit_button ?? true,
  showPdfButton: json?.show_pdf_button ?? false,
});

const MyPage = ({ currentUser }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [error, setError] = useState(false);

  // 指定したIDの要素まで window をスクロールする関数を取得
  const scrollToAnchor = useScrollToAnchor();

  const isDesktop = useIsDesktop();

  // URLから画面種別を決定
  const pageType = useMemo(
    () => getPageType(location.pathname, location.search, currentUser),
    [location.pathname, location.search, currentUser],
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // MyPage を再取得できる関数（削除後に使う）
  const fetchMyPageData = useCallback(() => {
    const url = buildSkillSheetApiUrl({ pageType, targetId: id });
    setLoading(true);
    setError(false);

    return fetch(url)
      .then((res) => res.json())
      .then((json) => setData(normalizeSkillSheetJson(json)))
      .catch((e) => {
        console.error(e);
        setError(true);
        toast.error("読み込みに失敗しました。", { toastId: "fetch-error" });
      })
      .finally(() => setLoading(false));
  }, [pageType, id]);

  useEffect(() => {
    fetchMyPageData();
  }, [fetchMyPageData]);

  // 編集対象（User / CompanyUser）
  const owner = useMemo(() => {
    if (!data?.profile) return null;
    return targetFromFormData(data.profile);
  }, [data?.profile]);

  if (error) {
    return (
      <Box p={4} ml={4}>
        <Typography>
          読み込みに失敗しました。
          <br />
          通信環境をご確認し、再読み込みしてください。
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  const {
    profile,
    skill,
    pdfDownloadUrl,
    workHistories,
    showEditButton,
    showPdfButton,
  } = data;

  // PDF出力
  const onPdfClick = () => {
    if (!pdfDownloadUrl) return;
    window.open(pdfDownloadUrl, "_blank", "noopener,noreferrer");
  };

  // 基本情報編集へ遷移
  const onEditClick = () => {
    if (!owner) return;

    // profile から owner（User / CompanyUser）を作る
    const path = editSkillSheetBasicsPathFor(owner);

    navigate(path);
  };

  return (
    <Stack spacing={1.5}>
      {/* ヘッダー */}
      <HeaderSection
        title={<SectionTitle title="マイページ" variant="page" />}
        leftActions={
          showPdfButton ? (
            <ActionButton
              label="PDF出力"
              icon={<PictureAsPdfIcon sx={{ color: JOB_COLOR }} />}
              onClick={onPdfClick}
              sx={{
                width: isDesktop ? 175 : "100%",
                minWidth: { sm: 175 },
                backgroundColor: JOB_COLOR,
                "&:hover": {
                  backgroundColor: JOB_COLOR_DARK,
                },
              }}
            />
          ) : null
        }
        notice={
          showEditButton ? (
            <NoticeBanner text="情報欄のベースを整え、第一印象を高めましょう。" />
          ) : null
        }
        rightActions={
          showEditButton ? (
            <ActionButton
              label="基本情報編集"
              icon={<EditIcon sx={{ color: PROJECT_COLOR }} />}
              onClick={onEditClick}
              sx={{
                backgroundColor: PROJECT_COLOR,
                "&:hover": {
                  backgroundColor: PROJECT_COLOR_DARK,
                },
              }}
            />
          ) : null
        }
      />

      {/* 基本情報 */}
      <ProfileSection profile={profile} />

      {/* ヒューマンスキル */}
      <HumanSkillSection humanSkills={profile.human_skills} />

      {/* 自己PR、自己実績 */}
      <ProfileTextSection profile={profile} />

      {/* 資格 (0件なら表示しない) */}
      {(profile?.qualifications ?? []).length > 0 && (
        <QualificationSection qualifications={profile?.qualifications} />
      )}

      {/* プロジェクト詳細 */}
      <div id="skill-sheet-section">
        {/* プロジェクト詳細 */}
        <SkillSheetMainSection
          workHistories={workHistories}
          showEditButton={showEditButton}
          owner={owner}
          onDeleted={async () => {
            await fetchMyPageData();
            window.dispatchEvent(new Event("app:refreshSidebarMenus"));

            scrollToAnchor("skill-sheet-section", { offset: 64 });
          }}
        />
      </div>

      {/* スキル */}
      <SkillSection
        skill={skill}
        showEditButton={showEditButton}
        owner={owner}
      />
    </Stack>
  );
};

export default MyPage;
